import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { Umbrella, X, Save, AlertTriangle, Info, Hash } from 'lucide-react';
import * as payrollApi from '../../../../services/api/payrollApi';

// InputField matches the pattern used by UpdateTaxProfileModal /
// UpdateCompensationModal — defined outside to avoid recreating on each render.
const InputField = ({ icon: Icon, label, name, type = 'number', required = false, description, value, onChange, showValidation, errors, ...props }) => (
  <div className="input-group">
    <label className="input-label" htmlFor={`input-${name}`}>
      {label}
      {required && <span className="required-indicator">*</span>}
    </label>
    {description && <p className="input-description">{description}</p>}
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      <input
        id={`input-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
        // Number inputs bump their value on scroll-wheel when focused; blur
        // on wheel so a normal page scroll doesn't silently edit the field.
        onWheel={type === 'number' ? (e) => e.currentTarget.blur() : undefined}
        {...props}
      />
    </div>
    {showValidation && errors?.[name] && (
      <div className="error-message">
        <AlertTriangle size={12} />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const UpdateVacationBalanceModal = ({ show, handleClose, employeeId, companyId, snapshot, onSuccess }) => {
  const [formData, setFormData] = useState({
    earnedDays: '',
    carriedOverDays: '',
    savedDays: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && snapshot) {
      setFormData({
        earnedDays: String(snapshot.earnedDays ?? 0),
        carriedOverDays: String(snapshot.carriedOverDays ?? 0),
        savedDays: String(snapshot.savedDays ?? 0),
      });
      setShowValidation(false);
      setErrors({});
      setError(null);
    }
  }, [show, snapshot]);

  const validateForm = () => {
    const newErrors = {};
    for (const key of ['earnedDays', 'carriedOverDays', 'savedDays']) {
      const n = Number(formData[key]);
      if (Number.isNaN(n) || n < 0) {
        newErrors[key] = 'Måste vara 0 eller större';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setShowValidation(true);
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    try {
      await payrollApi.setVacationBalance(companyId, {
        employeeId,
        year: snapshot.year,
        earnedDays: Number(formData.earnedDays),
        carriedOverDays: Number(formData.carriedOverDays),
        savedDays: Number(formData.savedDays),
      });
      if (onSuccess) onSuccess();
      resetAndClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Det gick inte att uppdatera semestersaldot');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    if (snapshot) {
      setFormData({
        earnedDays: String(snapshot.earnedDays ?? 0),
        carriedOverDays: String(snapshot.carriedOverDays ?? 0),
        savedDays: String(snapshot.savedDays ?? 0),
      });
    }
    setError(null);
    setShowValidation(false);
    setErrors({});
    handleClose();
  };

  return (
    <>
      {/* Shares the same style block as UpdateTaxProfileModal — kept local to
          avoid a cross-modal CSS dependency. */}
      <style>{`
        .vac-modal-content { border-radius: 12px; overflow: hidden; border: none; }
        .vac-modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 1.5rem 2rem; border-bottom: 1px solid #e2e8f0; position: relative;
        }
        .vac-modal-body { padding: 2rem; background: #fff; max-height: 70vh; overflow-y: auto; }
        .close-button {
          position: absolute; top: 1rem; right: 1.5rem;
          background: #f1f5f9; border: 1px solid #e2e8f0; color: #64748b;
          width: 32px; height: 32px; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease; cursor: pointer;
        }
        .close-button:hover { background: #e2e8f0; color: #475569; }
        .form-section { margin-bottom: 2rem; }
        .section-title {
          font-size: 1rem; font-weight: 600; color: #1e293b; margin-bottom: 1.5rem;
          display: flex; align-items: center; gap: 0.5rem;
          padding-bottom: 0.75rem; border-bottom: 1px solid #f1f5f9;
        }
        .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; }
        .input-group { display: flex; flex-direction: column; gap: 0.5rem; }
        .input-label { font-size: 0.875rem; font-weight: 500; color: #374151; margin: 0; }
        .required-indicator { color: #ef4444; margin-left: 0.25rem; }
        .input-description { font-size: 0.75rem; color: #6b7280; margin: 0; line-height: 1.4; }
        .input-container { position: relative; display: flex; align-items: center; }
        .input-icon { position: absolute; left: 0.75rem; z-index: 1; color: #6b7280; pointer-events: none; }
        .modern-input {
          width: 100%; padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.875rem;
          background: white; color: #374151; transition: all 0.2s ease;
        }
        .modern-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); }
        .modern-input.error { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1); }
        .error-message { display: flex; align-items: center; gap: 0.5rem; color: #ef4444; font-size: 0.75rem; font-weight: 500; }
        .current-value-card {
          background: #ecfeff; border: 1px solid #67e8f9; border-radius: 8px;
          padding: 1rem; margin-bottom: 1.5rem;
          display: flex; align-items: start; gap: 0.75rem;
        }
        .current-value-icon { color: #0891b2; flex-shrink: 0; margin-top: 0.125rem; }
        .current-value-content { flex: 1; }
        .current-value-label {
          font-size: 0.75rem; color: #155e75; font-weight: 500;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem;
        }
        .current-value-text { font-size: 0.95rem; font-weight: 600; color: #0e7490; margin: 0; }
        .validation-summary {
          background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px;
          padding: 1rem; margin-bottom: 1.5rem;
          display: flex; align-items: start; gap: 0.75rem;
        }
        .validation-icon { color: #ef4444; flex-shrink: 0; margin-top: 0.125rem; }
        .validation-content { flex: 1; }
        .validation-title { font-size: 0.875rem; font-weight: 600; color: #dc2626; margin-bottom: 0.5rem; }
        .form-info {
          background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
          padding: 1rem; margin-bottom: 1.5rem;
          display: flex; align-items: start; gap: 0.75rem;
        }
        .info-icon { color: #0284c7; flex-shrink: 0; margin-top: 0.125rem; }
        .info-text { font-size: 0.875rem; color: #0c4a6e; margin: 0; }
        .modal-actions {
          background: #f8fafc; padding: 1.5rem 2rem; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: space-between; align-items: center;
        }
        .action-button {
          border-radius: 8px; padding: 0.75rem 1.5rem; font-weight: 600;
          font-size: 0.875rem; border: none; transition: all 0.2s ease;
          display: flex; align-items: center; gap: 0.5rem; cursor: pointer;
        }
        .btn-cancel { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
        .btn-cancel:hover { background: #e2e8f0; color: #475569; }
        .btn-save { background: var(--bs-primary); color: white; }
        .btn-save:hover { background: var(--bs-primary); opacity: 0.9; }
        .btn-save:disabled { background: #94a3b8; cursor: not-allowed; }
        .loading-spinner {
          width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid white; border-radius: 50%; animation: spin 1s linear infinite;
        }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <Modal show={show} onHide={resetAndClose} size="lg" backdrop="static" centered>
        <div className="vac-modal-content">
          {/* Header */}
          <div className="vac-modal-header">
            <button className="close-button" onClick={resetAndClose} aria-label="Stäng modal">
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <Umbrella size={24} color="#0891b2" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  Redigera semestersaldo
                </h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  {snapshot
                    ? `Semesterår ${snapshot.year}/${snapshot.year + 1} (${snapshot.dateRange?.from} – ${snapshot.dateRange?.to})`
                    : 'Aktuellt semesterår'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="vac-modal-body">
            <Form onSubmit={handleSubmit}>
              {error && (
                <div className="validation-summary">
                  <AlertTriangle size={16} className="validation-icon" />
                  <div className="validation-content">
                    <div className="validation-title">Ett fel uppstod</div>
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>{error}</p>
                  </div>
                </div>
              )}

              {/* Read-only context: what's currently on file */}
              {snapshot && (
                <div className="current-value-card">
                  <Umbrella size={20} className="current-value-icon" />
                  <div className="current-value-content">
                    <div className="current-value-label">Nuvarande saldo</div>
                    <div className="current-value-text">
                      Tjänat {snapshot.earnedDays} · Överförda {snapshot.carriedOverDays} · Sparat {snapshot.savedDays}
                      {' — '}Använd {snapshot.usedDays} · Kvar {snapshot.remainingPaidDays}
                      {(snapshot.advanceDaysTaken || 0) > 0 && ` · Förskott ${snapshot.advanceDaysTaken}`}
                    </div>
                  </div>
                </div>
              )}

              <div className="form-info">
                <Info size={16} className="info-icon" />
                <div className="info-text">
                  <strong>Information:</strong> Endast <em>Tjänat</em>, <em>Överförda</em> och <em>Sparat</em> redigeras här.
                  <em> Använd</em> beräknas automatiskt från godkända SEMESTER-poster på lönespecar.
                </div>
              </div>

              <div className="form-section">
                <div className="form-grid">
                  <InputField
                    icon={Hash}
                    label="Tjänat"
                    name="earnedDays"
                    type="number"
                    required={true}
                    placeholder="25"
                    description="Intjänade dagar från föregående intjänandeår (§4+§7)"
                    value={formData.earnedDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                  <InputField
                    icon={Hash}
                    label="Överförda"
                    name="carriedOverDays"
                    type="number"
                    required={true}
                    placeholder="0"
                    description="Oanvända dagar överförda från tidigare år"
                    value={formData.carriedOverDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                  <InputField
                    icon={Hash}
                    label="Sparat"
                    name="savedDays"
                    type="number"
                    required={true}
                    placeholder="0"
                    description="Sparade dagar enligt §18 (max 5/år i ackumulering)"
                    value={formData.savedDays}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.5"
                  />
                </div>
              </div>
            </Form>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button className="action-button btn-cancel" onClick={resetAndClose} disabled={loading}>
                Avbryt
              </button>
              <button className="action-button btn-save" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Spara semestersaldo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateVacationBalanceModal;
